import { useMutation } from '@apollo/client';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import ErrorState from 'Components/ErrorState';
import FlexBox from 'Components/FlexBox';
import Loader from 'Components/Forms/Loader';
import PageContainer from 'Components/PageContainer';
import PageTitle from 'Components/PageTitle';
import { useBlockNavigation } from 'Contexts/BlockNavigationContext';
import { ThemeTypography } from 'DesignSystem';
import AdvancedLoader from 'DesignSystem/Overlays/AdvancedLoader/AdvancedLoader';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import DatasetForm from 'FeatureModules/ComponentChain/Components/Forms/DatasetForm';
import DatasetValidation, {
  IDatasetTableHandle,
} from 'FeatureModules/DataImport/Components/Forms/DatasetValidation';
import DatasetVerification from 'FeatureModules/DataImport/Components/Views/DatasetVerification';
import useDatasetMutation from 'FeatureModules/DataImport/hooks/useDatasetMutation';
import useDatasetOverview from 'FeatureModules/DataImport/hooks/useDatasetOverview';
import {
  datasetSchema,
  IDatasetFormValue,
  IGeoUploadFormValues,
} from 'FeatureModules/DataImport/schemas/geoUpload.schema';
import { IOriginTableSettings } from 'FeatureModules/DataImport/types/dataImport.types';
import { transformColumnNamesToTableSettingColumns } from 'FeatureModules/DataImport/utils/dataset.utils';
import useDocumentUpload from 'FeatureModules/Document/hooks/useDocumentUpload';
import useUploadState from 'FeatureModules/Document/hooks/useUploadState';
import { EDocumentCategory } from 'FeatureModules/Document/types/document.types';
import { Formik, FormikProps } from 'formik';
import { DELETE_DOCUMENTS } from 'graphql/secure/mutations';
import useDialog from 'hooks/useDialog';
import useMessages from 'hooks/useMessages';
import usePageTitle from 'hooks/usePageTitle';
import isEqual from 'lodash/isEqual';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useMeasure from 'react-use/lib/useMeasure';
import { useQueryParam } from 'use-query-params';
import { originTableSettingsSchema } from '../schemas/dataImport.schemas';
import { EGeoDataStatus } from '../types/dataset.types';

const Container = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(0.5),
  marginTop: theme.spacing(2),
}));

const DatasetOverview: FC = () => {
  usePageTitle('Origin data import');
  const [defaultClusterId] = useQueryParam<string>('clusterId');
  const {
    dataset,
    partner,
    partnerId,
    error,
    loading,
    isPartnerLibrary,
    isDatasetCreation,
    refetch,
  } = useDatasetOverview();
  const [uploadFile] = useDocumentUpload();
  const { createDataset, updateDataset } = useDatasetMutation();
  const { startDatasetPreProcessing } = useDatasetMutation();
  const { handleCleanCreatedRecords, cleanFiles } = useUploadState();
  const { openDialog } = useDialog();
  const { setErrorMessage, setSuccessMessage } = useMessages();
  const location = useLocation();

  const datasetTableRef = useRef<IDatasetTableHandle>(null);
  const formRef = useRef<FormikProps<IDatasetFormValue>>(null);
  const [deleteDocument] = useMutation(DELETE_DOCUMENTS);

  const [containerRef, { height: containerHeight }] = useMeasure<HTMLDivElement>();

  const [tableSettings, setTableSettings] = useState<IOriginTableSettings>(
    originTableSettingsSchema.default()
  );

  const { block, unblock } = useBlockNavigation();

  /**
   * Initial cleanup of files that were uploaded but not attached to a dataset
   */
  useEffect(() => {
    cleanFiles();
    handleCleanCreatedRecords();
    return () => unblock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      !error &&
      (dataset?.status === EGeoDataStatus.CREATED ||
        dataset?.status === EGeoDataStatus.VALIDATED ||
        dataset?.status === EGeoDataStatus.VALIDATION_FAILED)
    ) {
      block();
      // Form data has no changes
    } else if (isEqual(formRef.current?.values, formRef.current?.initialValues)) {
      unblock();
    }
  }, [formRef, dataset, error, block, unblock]);

  const initialValues: IDatasetFormValue = {
    ...datasetSchema.default(),
    ...(partner ? { ownedBy: partner } : {}),
    ...(dataset
      ? {
          datasetId: dataset.id,
          title: dataset.title,
          rawMaterialId: dataset.rawMaterial?.id,
          countryCode: dataset.originCountry,
          ownedBy: dataset.ownedBy,
        }
      : {}),
  };

  const blockNavigation = () => {
    block({
      alertDialogConfig: {
        title: 'Discard dataset',
        content:
          'Your dataset has formatting errors and can not be saved until these errors are corrected. If you exit without saving, your data import will be lost. Do you want to continue to edit your import?',
      },
    });
  };

  const unblockAndUpdateNavigation = useCallback(
    (datasetId: string) => {
      // Finally let the router detect the URL changes that could have been blocked so far from the empty table creation
      if (!location.pathname.match(/\/datasets\/[0-9a-fA-F-]{36}/)) {
        unblock({ pathname: `${location.pathname}/${datasetId}` });
      } else {
        unblock(location);
      }
    },
    [location, unblock]
  );

  const updateDatasetIfNeeded = async (
    values: Partial<
      Pick<IGeoUploadFormValues, 'title' | 'rawMaterialId' | 'countryCode' | 'ownedBy'>
    > & {
      datasetId: string; // datasetid is required
      files?: { id: string }[];
    }
  ) => {
    if (values.datasetId && !isEqual(initialValues, values)) {
      return await updateDataset({
        ...values,
      });
    }
  };

  const handleSubmit = async (values: IDatasetFormValue) => {
    if (!formRef.current) {
      return;
    }
    formRef.current.setSubmitting(true);

    // If the dataset is already after normalization, update it and run the processing again
    if (
      dataset?.id &&
      (dataset?.status === EGeoDataStatus.VERIFICATION_FAILED_WITH_ERRORS ||
        dataset?.status === EGeoDataStatus.VERIFICATION_FAILED_WITH_WARNINGS ||
        dataset?.status === EGeoDataStatus.COMPLETED)
    ) {
      formRef.current.setSubmitting(true);
      const updateDatasetResponse = await updateDatasetIfNeeded({
        ...values,
        datasetId: dataset.id,
      });
      formRef.current.setSubmitting(false);
      if (updateDatasetResponse?.data?.updateDataset) {
        setSuccessMessage('Dataset successfully updated');
      }
      return;
    }

    // Validate / export table data
    try {
      let currentDatasetId: string | undefined = values.datasetId;

      if (
        !datasetTableRef.current ||
        !datasetTableRef.current.getDataAsCsv ||
        !datasetTableRef.current.runValidations
      ) {
        throw new Error('Could not access the table data');
      }

      // Run validations
      const isValid = datasetTableRef.current.runValidations();

      if (!isValid) {
        setErrorMessage('Please correct the validation errors in the table');
        formRef.current.setSubmitting(false);
        return;
      }

      // Extract data as csv from table representation
      const csvData = datasetTableRef.current.getDataAsCsv({
        removeFirstAndLastColumn: true,
        // By default the column id is used as header and adds a space based on the camel case
        processHeaderCallback: ({ column }) => column.getColId(),
      });

      // Create a file from the csv data
      const file = new File([csvData], `${values.title}-validated.csv`, {
        type: 'text/csv',
      });

      // TODO: Fix the useDocumentUpload.js file to handle the upload process correctly with promises
      const uploadDocumentPromise = () =>
        new Promise<{ record?: { id: string } } | undefined>(async (resolve, reject) => {
          // Upload the file to the document library
          // @ts-ignore
          const recordResult = await uploadFile({
            file,
            category: EDocumentCategory.GEOGRAPHICAL_FEATURES,
            onSuccess: () => resolve(recordResult),
            onError: () => reject('Uploading document failed'),
          });
        });

      const documentCreationResponse = await uploadDocumentPromise();

      if (!documentCreationResponse?.record?.id) {
        throw new Error('Could not create the document');
      }
      // Create dataset if datasetId does not exist in the form
      // We do not want to update the cache here since the dataset ends up in status CREATED until the processing is done.
      // This created a flickering effect and the dataset gets anyway update in the processing
      if (!currentDatasetId) {
        const datasetCreationResult = await createDataset({
          ...values,
          files: [{ id: documentCreationResponse.record.id }],
        });
        currentDatasetId = datasetCreationResult.data?.createDataset.dataset.id;
        refetch({ id: currentDatasetId });
        // Routing to the new dataset without performing a rerender that would reset the form
        window.history.replaceState(null, '', `datasets/${currentDatasetId}`);
        // Update the form with the new dataset id
        formRef.current.setValues({ ...formRef.current.values, datasetId: currentDatasetId });
      } else {
        // Delete old document on the dataset
        if (dataset?.documents?.length) {
          deleteDocument({ variables: { ids: dataset.documents.map(({ id }) => id) } });
        }
        // We do not want to update the cache here since the dataset ends up in status CREATED until the processing is done.
        // This created a flickering effect and the dataset gets anyway update in the processing
        await updateDatasetIfNeeded({
          ...values,
          datasetId: currentDatasetId,
          files: [{ id: documentCreationResponse.record.id }],
        });
      }
      if (!currentDatasetId) {
        throw new Error('Could not reference the dataset correctly');
      }
      const datasetId = currentDatasetId;
      const mappedColumns = datasetTableRef.current.getColumnsMapping();
      const preProcessingResponse = await startDatasetPreProcessing({
        datasetId,
        mappedColumns,
      });
      if (
        preProcessingResponse.data?.preProcessDataset.datasetProcessingError ||
        preProcessingResponse.data?.preProcessDataset.dataset.status !== EGeoDataStatus.VALIDATED
      ) {
        // Reset open geometry state
        datasetTableRef.current?.resetOpenGeometryErrors();
        formRef.current?.setSubmitting(false);
        setErrorMessage('Please correct all validation errors in the table');
        unblockAndUpdateNavigation(datasetId);
        return;
      }
      formRef.current?.setSubmitting(false);
      // Create / update the dataset and show confirmation dialogs
      openDialog({
        type: 'CONFIRM_DATASET_IMPORT',
        props: {
          values: {
            overwriteExisting: tableSettings.overwriteExisting,
            clusterSites: tableSettings.clusterSites,
            siteClusterId: defaultClusterId,
          },
          dataset: {
            mappedColumns,
            datasetId,
            countryCode: values.countryCode,
            rawMaterialId: values.rawMaterialId,
          },
          onProcessingStart: () => formRef.current?.setSubmitting(true),
          onProcessingComplete: () => {
            formRef.current?.setSubmitting(false);
            unblockAndUpdateNavigation(datasetId);
          },
        },
      });
    } catch (error) {
      console.error('Error while saving the dataset', error);
      formRef.current?.setSubmitting(false);
      setErrorMessage('Could not save the dataset. Please try again or contact the support');
    }
  };

  const onTableSettingsChange = useCallback(
    (settings: IOriginTableSettings) => {
      setTableSettings(settings);
    },
    [setTableSettings]
  );

  const handleOpenSettings = () => {
    openDialog({
      type: 'DATASET_TABLE_SETTINGS',
      props: { settings: tableSettings, onTableSettingsChange },
    });
  };

  const handleClickDownload = async () => {
    if (!dataset?.originalGeoDataId) {
      return;
    }
    openDialog({
      type: 'DOWNLOAD_GEO_DATA',
      props: {
        geoDataId: dataset?.originalGeoDataId,
        disableGeojson:
          dataset?.status === EGeoDataStatus.VALIDATION_FAILED ||
          dataset?.status === EGeoDataStatus.VERIFICATION_FAILED_WITH_WARNINGS,
        validationOnly:
          dataset?.status === EGeoDataStatus.VERIFICATION_FAILED_WITH_ERRORS ||
          dataset?.status === EGeoDataStatus.VERIFICATION_FAILED_WITH_WARNINGS ||
          dataset?.status === EGeoDataStatus.COMPLETED
            ? false
            : true,
      },
    });
  };

  /**
   * Initially enable the columns in the tableSettings that are already included in the dataset
   */
  useEffect(() => {
    const datasetTableRepresentation = dataset?.tableRepresentation;
    if (
      (dataset?.status === EGeoDataStatus.VALIDATION_FAILED ||
        dataset?.status === EGeoDataStatus.VALIDATED) &&
      datasetTableRepresentation
    ) {
      setTableSettings(settings => ({
        ...settings,
        columns: transformColumnNamesToTableSettingColumns(datasetTableRepresentation.columnsNames),
      }));
    }
  }, [dataset]);

  if (
    loading ||
    (!isDatasetCreation &&
      (!dataset ||
        dataset?.status === EGeoDataStatus.VALIDATED ||
        dataset?.status === EGeoDataStatus.VALIDATION_FAILED) &&
      !dataset?.tableRepresentation)
  ) {
    return <Loader />;
  }
  if (error) {
    return <ErrorState />;
  }

  return (
    <PageContainer>
      <Box ref={containerRef}>
        <Box mb={2}>
          <PageTitle
            title="Origin data import"
            goBackLabel="All datasets"
            goBackUrl={isPartnerLibrary ? `/partners/${partnerId}/imports` : '/imports'}
            titleExtension={isPartnerLibrary ? partner?.name : undefined}
            TitleAdornment={
              <FlexBox ml={1} mt={0.5}>
                <Icon name="farm" color="gray-60" size="large" />
                <Box mr={0.5} />
                <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_60">
                  Farm data
                </ThemeTypography>
              </FlexBox>
            }
          />
        </Box>

        <Formik<IDatasetFormValue>
          innerRef={formRef}
          enableReinitialize
          validationSchema={datasetSchema}
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          {props => (
            <>
              {props.isSubmitting && <AdvancedLoader />}

              <DatasetForm
                {...props}
                isTableView={
                  !dataset ||
                  dataset?.status === EGeoDataStatus.CREATED ||
                  dataset?.status === EGeoDataStatus.VALIDATED ||
                  dataset?.status === EGeoDataStatus.VALIDATION_FAILED
                }
                disableOwnerSelection={
                  isPartnerLibrary || dataset?.status === EGeoDataStatus.COMPLETED
                }
                onTableSettingsClick={handleOpenSettings}
                onFormChange={blockNavigation}
              />
            </>
          )}
        </Formik>
      </Box>

      {/* Subtract the top container height - 56px margin & padding to use the available height for the table */}
      <Container height={`calc(100% - ${containerHeight}px - 56px)`} minHeight={600}>
        {/* Show either the validation / empty table or the verification screen */}
        {dataset?.status === EGeoDataStatus.VERIFICATION_FAILED_WITH_ERRORS ||
        dataset?.status === EGeoDataStatus.VERIFICATION_FAILED_WITH_WARNINGS ||
        dataset?.status === EGeoDataStatus.COMPLETED ? (
          <DatasetVerification
            dataset={dataset}
            issues={dataset.issues}
            pageSubTitle="Farms in dataset"
          />
        ) : (
          <DatasetValidation
            datasetTableRef={datasetTableRef}
            tableRepresentation={dataset?.tableRepresentation}
            tableSettings={tableSettings}
            height="100%"
            mode="add-edit"
            onClickDownload={handleClickDownload}
          />
        )}
      </Container>
    </PageContainer>
  );
};

export default DatasetOverview;
